import { EditOutlined, ExperimentFilled, WarningFilled } from '@ant-design/icons'
import { defaultDateDef, defaultNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { endOfTime, type SupplyDirectoryHeaderFilters } from '@components/SupplyManagement/types'
import { startEndFormatter } from '@components/SupplyManagement/util'
import { DeltaTag, GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import type { ColDef } from 'ag-grid-community'
import { Popover, Tag, Tooltip } from 'antd'
import { pick, startCase } from 'lodash'
import moment from 'moment'
import React, { useState } from 'react'

import { StatusRenderer } from './CellRenderers/StatusRenderer'
import { PriceHistoryButton } from './components/PriceHistoryButton'
import { formatDecimal } from '@/api/useFormatter'
import { DirectiveGrouping } from '@/components/_SiteWide/OrderDisplay/DirectiveGrouping'
import { NumberCellEditor } from '@/components/_SiteWide/Grid/CellEditors/NumberCellEditor'
import { isDefined } from '@/utils/general'
import { DirectiveDeltaTag } from '@/components/Reports/DispatchDirective/Grid/DirectiveDeltaTag'

export function getColumnDefs(
  showCarrierView: boolean,
  toggleDrawer: (price: Gravitate.SupplyManagementOption) => void,
  formattedCurrency: string,
  openEditOutageDrawer: (supplyoutage: Gravitate.SupplyOutage) => void,
  cancelOutage: (outageId: string) => void,
  headerFilters: SupplyDirectoryHeaderFilters
): ColDef<Gravitate.SupplyManagementOption>[] {
  return [
    {
      field: 'enabled',
      headerName: 'Status',
      suppressColumnsToolPanel: true,
      cellRenderer: ({ value }) => (
        <StatusRenderer value={value} openEditOutageDrawer={openEditOutageDrawer} cancelOutage={cancelOutage} />
      ),
      comparator: (valueA, valueB) => {
        if (valueA.enabled && !valueB.enabled) return -1
        if (!valueA.enabled && valueB.enabled) return 1
        return 0
      },
      valueFormatter: ({ value }) => (value.enabled ? 'Enabled' : 'Disabled'),
      filterValueGetter: ({ data }) => (data.enabled ? 'Enabled' : 'Disabled'),
      valueGetter: ({ data }) =>
        pick(data, [
          'enabled',
          'outages',
          'terminal_id',
          'terminal',
          'product_id',
          'product',
          'product_group',
          'supplier_id',
          'supplier',
          'price_type',
          'contract',
          'counterparty_id',
          'counterparty',
        ]),
      minWidth: 110,
      editable: false,
    },
    {
      field: 'key',
      headerName: '',
      menuTabs: [],
      suppressMenu: true,
      suppressColumnsToolPanel: true,
      filter: false,
      cellRenderer: ({ data }) => <PriceHistoryButton handleShowDrawer={() => toggleDrawer(data)} />,
      maxWidth: 60,
      sortable: false,
      editable: false,
    },
    {
      field: 'terminal',
      rowGroup: true,
      initialHide: true,
      minWidth: 100,
      editable: false,
    },
    {
      field: 'product',
      rowGroup: true,
      initialHide: true,
      minWidth: 100,
      editable: false,
    },
    {
      field: 'product_group',
      maxWidth: 100,
      headerName: 'Group',
      initialHide: true,
      editable: false,
    },
    {
      field: 'supplier',
      minWidth: 100,
      editable: false,
    },
    {
      field: 'price_type',
      minWidth: 100,
      headerName: 'Price Type',
      editable: false,
    },
    {
      field: 'city',
      headerName: 'City',
      initialHide: true,
      editable: false,
    },
    {
      field: 'supply_zone',
      headerName: 'Supply Zone',
      initialHide: true,
      editable: false,
    },
    {
      field: 'price',
      sort: headerFilters.groupBy === 'site' ? null : 'asc',
      minWidth: 140,
      initialHide: showCarrierView,
      ...defaultNumberDef('price', 4),
      filterValueGetter: ({ data }) => (data?.price ? parseFloat(data.price) : null),
      comparator: (valueA, valueB) => {
        return parseFloat(valueA) - parseFloat(valueB)
      },
      editable: false,
    },
    {
      field: 'total_price',
      headerName: 'Total Price',
      sort: headerFilters.groupBy === 'site' ? 'asc' : null,
      minWidth: 160,
      hide: headerFilters.groupBy !== 'site',
      ...defaultNumberDef('total_price', 4),
      filterValueGetter: ({ data }) => (data?.total_price ? parseFloat(data.total_price) : null),
      comparator: (valueA, valueB) => {
        return parseFloat(valueA) - parseFloat(valueB)
      },
      editable: false,
    },
    {
      field: 'freight',
      headerName: 'Freight',
      headerTooltip: 'Rate per gallon',
      hide: headerFilters.groupBy !== 'site',
      valueGetter: ({ data }) => data?.freight && formatDecimal(data?.freight, 4, false),
      editable: false,
    },
    {
      field: 'loaded_miles',
      headerName: 'Miles',
      headerTooltip: 'Loaded miles',
      hide: headerFilters.groupBy !== 'site',
      valueGetter: ({ data }) => data?.loaded_miles && formatDecimal(data?.loaded_miles, 4, false),
      editable: false,
    },
    {
      field: 'loaded_minutes',
      headerName: 'Minutes',
      headerTooltip: 'Loaded minutes',
      hide: headerFilters.groupBy !== 'site',
      valueGetter: ({ data }) => data?.loaded_minutes && formatDecimal(data?.loaded_minutes, 4, false),
      editable: false,
    },
    {
      field: 'delta_to_low',
      headerName: 'Delta to Low',
      minWidth: 140,
      filter: 'agNumberColumnFilter',
      valueGetter: ({ data }) => (data?.delta_to_low ? parseFloat(data.delta_to_low?.toFixed(4)) : null),
      cellRenderer: ({ value }) => (
        <DeltaTag
          value={value || 0}
          threshold={0}
          textIfEqual='Low Price'
          prefix={formattedCurrency}
          precision={4}
          invertColors
        />
      ),
      editable: false,
    },
    {
      field: 'delta_to_contract',
      headerName: 'Delta to Contract',
      minWidth: 140,
      filter: 'agNumberColumnFilter',
      valueGetter: ({ data }) => (data?.delta_to_contract ? parseFloat(data.delta_to_contract?.toFixed(4)) : null),
      cellRenderer: ({ value }) =>
        value && (
          <DeltaTag
            value={value || 0}
            threshold={0}
            textIfEqual='Low Price'
            prefix={formattedCurrency}
            precision={4}
            invertColors
          />
        ),
      initialHide: true,
      editable: false,
    },
    {
      field: 'delta_to_inventory',
      headerName: 'Delta to Inventory',
      minWidth: 140,
      filter: 'agNumberColumnFilter',

      valueGetter: ({ data }) => (data?.delta_to_inventory ? parseFloat(data.delta_to_inventory?.toFixed(4)) : null),
      cellRenderer: ({ value }) =>
        value && (
          <DeltaTag
            value={value || 0}
            threshold={0}
            textIfEqual='Low Price'
            prefix={formattedCurrency}
            precision={4}
            invertColors
          />
        ),
      initialHide: true,
      editable: false,
    },
    {
      field: 'directive',
      headerName: 'Directive',
      cellRenderer: ({ value, data }) => {
        const [open, setOpen] = useState(false)
        if (!data.multiple_prices) {
          return <Texto>{value}</Texto>
        }
        return (
          <Popover
            content={<DirectiveGrouping row={data} as_of={headerFilters.filter.as_of} />}
            trigger='click'
            placement='top'
            open={open}
            onOpenChange={setOpen}
          >
            <GraviButton appearance='text'>
              <Texto appearance='primary' weight='bold' style={{ cursor: 'pointer' }}>
                <ExperimentFilled /> {value}
              </Texto>
            </GraviButton>
          </Popover>
        )
      },
      editable: false,
    },
    {
      field: 'directive_min',
      headerName: 'Dir. Min',
      editable: ({ data }) => !!data.group_id,
      headerComponent: () => (
        <Texto appearance='hint' weight='bold'>
          <EditOutlined /> DIR. MIN
        </Texto>
      ),
      valueGetter: ({ data }) => (data?.directive_min ? formatDecimal(data?.directive_min, 0, false) : null),
      cellEditor: NumberCellEditor,
      filter: 'agNumberColumnFilter',
      cellEditorParams: {
        numberType: 'int',
        minValue: 0,
      },
      cellRenderer: ({ value }) => {
        if (!isDefined(value)) return null
        return <Tooltip title={'Double-click to edit'}>{value}</Tooltip>
      },
      minWidth: 120,
    },
    {
      field: 'directive_max',
      headerName: 'Dir. Max',
      editable: ({ data }) => !!data.group_id,
      headerComponent: () => (
        <Texto appearance='hint' weight='bold'>
          <EditOutlined /> DIR. MAX
        </Texto>
      ),
      valueGetter: ({ data }) => data?.directive_max && formatDecimal(data?.directive_max, 0, false),
      cellEditor: NumberCellEditor,
      filter: 'agNumberColumnFilter',
      cellEditorParams: {
        numberType: 'int',
        minValue: 0,
      },
      cellStyle: { textAlign: 'right' },
      cellRenderer: ({ value }) => {
        if (!isDefined(value)) return null
        return <Tooltip title={'Double-click to edit'}>{value}</Tooltip>
      },
      minWidth: 120,
    },
    {
      field: 'remaining',
      headerName: 'Remaining',
      headerTooltip: 'Directive daily total minus max',
      valueGetter: ({ data }) => {
        if (headerFilters.includePlannedVolume) {
          return data?.remaining_with_planned
        }
        return data?.remaining
      },
      cellRenderer: ({ value }) => {
        if (!isDefined(value)) return null
        return <DirectiveDeltaTag delta={value} />
      },
      minWidth: 160,
      editable: false,
    },
    {
      field: 'loaded',
      headerName: 'Loaded',
      cellStyle: { textAlign: 'right' },
      valueGetter: ({ data }) => (data?.loaded ? formatDecimal(data?.loaded, 0, false) : null),
      editable: false,
    },
    {
      field: 'scheduled',
      headerName: 'Scheduled',
      cellStyle: { textAlign: 'right' },
      valueGetter: ({ data }) => (data?.scheduled ? formatDecimal(data?.scheduled, 0, false) : null),
      editable: false,
    },

    {
      field: 'planned',
      headerName: 'Planned',
      cellStyle: { textAlign: 'right' },
      valueGetter: ({ data }) => (data?.planned ? formatDecimal(data?.planned, 0, false) : null),
      editable: false,
    },
    {
      field: 'carrier',
      headerName: 'Carrier',
      cellStyle: { textAlign: 'right' },
      valueGetter: ({ data }) => (data?.carrier ? formatDecimal(parseInt(data?.carrier), 0, false) : null),
      editable: false,
    },
    {
      field: 'total_with_planned',
      headerName: 'Total',
      valueGetter: ({ data }) => {
        if (headerFilters.includePlannedVolume && data?.total_with_planned) {
          return formatDecimal(data?.total_with_planned, 0, false)
        }
        if (data?.total_no_planned) {
          return formatDecimal(data?.total_no_planned, 0, false)
        }
        return null
      },
      filter: 'agNumberColumnFilter',
      cellStyle: { textAlign: 'right' },
      editable: false,
    },
    {
      field: 'effective_from',
      headerName: 'Effective From',
      ...defaultDateDef('effective_from'),
      minWidth: 140,
      initialWidth: 200,
      editable: false,
    },
    {
      field: 'effective_to',
      headerName: 'Effective To',
      minWidth: 140,
      initialWidth: 200,
      ...defaultDateDef('effective_to'),
      editable: false,
    },
    {
      field: 'created_date',
      headerName: 'Created Date',
      valueFormatter: (params) => formatters.SHORT_DATE_TIME(params.value),
      minWidth: 140,
      initialHide: true,
      editable: false,
    },
    {
      field: 'contract',
      minWidth: 140,
      cellRenderer: ({ value }) =>
        value &&
        value !== '' && (
          <Horizontal horizontalCenter>
            <Tag style={{ fontWeight: 'bold', width: '100px' }}>{value}</Tag>
          </Horizontal>
        ),
      editable: false,
    },
    {
      field: 'counterparty',
      flex: 0,
      initialWidth: 140,
      editable: false,
    },
    {
      field: 'expired_warning',
      headerName: 'Expired',
      menuTabs: [],
      suppressMenu: false,
      suppressColumnsToolPanel: false,
      filter: true,
      maxWidth: 100,
      cellRenderer: ({ value }) =>
        value && (
          <Texto appearance='warning' category='h5'>
            <Tooltip title='Expired Price'>
              <WarningFilled />
            </Tooltip>
          </Texto>
        ),

      initialHide: true,
      editable: false,
    },
    {
      field: 'outageReason',
      minWidth: 140,
      headerName: 'Outage Reason',
      valueGetter: ({ data }) => (data?.outages?.[0]?.status === 'active' ? data?.outages?.[0]?.reason_code : null),
      headerTooltip: 'Current Outage Reason',
      initialHide: true,
      editable: false,
    },
    {
      field: 'outageStart',
      minWidth: 140,
      headerName: 'Outage Start',
      cellRenderer: ({ data, value }) => {
        const outage = data?.outages?.[0]
        return value ? startEndFormatter(outage?.schedule_local.schedule_type, value, outage?.timezone) : null
      },
      valueGetter: ({ data }) =>
        data?.outages?.[0]?.status === 'active' ? data?.outages?.[0]?.schedule_local.start_date : null,
      headerTooltip: 'Current Outage Start',
      initialHide: true,
    },
    {
      field: 'outageEnd',
      minWidth: 140,
      headerName: 'Outage End',
      cellRenderer: ({ data, value }) => {
        const outage = data?.outages?.[0]
        return value ? startEndFormatter(outage?.schedule_local.schedule_type, value, outage?.timezone) : null
      },
      valueGetter: ({ data }) => {
        if (data?.outages?.[0]?.status === 'active' && data?.outages?.[0]?.schedule_local.end_date) {
          return data?.outages?.[0]?.schedule_local.end_date
        }
        if (data?.outages?.[0]?.status === 'active' && data?.outages?.[0]?.schedule_local.start_date) {
          return endOfTime
        }
        return null
      },
      headerTooltip: 'Current Outage End',
      initialHide: true,
    },
    {
      field: 'outageSchedule',
      minWidth: 140,
      headerName: 'Outage Schedule',
      valueGetter: ({ data }) =>
        data?.outages?.[0]?.status === 'active' ? startCase(data?.outages?.[0]?.schedule_local.schedule_type) : null,
      headerTooltip: 'Current Outage Schedule Type',
      initialHide: true,
      editable: false,
    },
    {
      field: 'outageTimezone',
      minWidth: 140,
      headerName: 'Timezone',
      valueGetter: ({ data }) => data?.outages?.[0]?.timezone,
      headerTooltip: 'Current Outage Timezone',
      initialHide: true,
      editable: false,
    },
    {
      field: 'outageNextReason',
      minWidth: 140,
      headerName: 'Next Reason',
      valueGetter: ({ data }) => {
        const outageIdx = data?.outages?.[0]?.status === 'active' ? 1 : 0
        return data?.outages?.[outageIdx]?.reason_code
      },
      headerTooltip: 'Next Outage Reason',
      initialHide: true,
      editable: false,
    },
    {
      field: 'outageNextStart',
      minWidth: 140,
      headerName: 'Next Start',
      cellRenderer: ({ data, value }) => {
        const outageIdx = data?.outages?.[0]?.status === 'active' ? 1 : 0
        return startEndFormatter(data?.outages?.[outageIdx]?.schedule_local.schedule_type, value)
      },
      valueGetter: ({ data }) => {
        const outageIdx = data?.outages?.[0]?.status === 'active' ? 1 : 0
        return data?.outages?.[outageIdx] && moment(data?.outages?.[1]?.schedule_local.start_date)
      },
      headerTooltip: 'Next Outage Start',
      initialHide: true,
      editable: false,
    },
    {
      field: 'outageNextEnd',
      minWidth: 140,
      headerName: 'Next End',
      cellRenderer: ({ data, value }) => startEndFormatter(data?.outages?.[1]?.schedule_local.end_date, value),
      valueGetter: ({ data }) => {
        const outageIdx = data?.outages?.[0]?.status === 'active' ? 1 : 0
        if (data?.outages?.[outageIdx]?.schedule_local.end_date) {
          return moment(data?.outages?.[outageIdx]?.schedule_local.end_date)
        }
        if (data?.outages?.[outageIdx]?.schedule_local.start_date) {
          return endOfTime
        }
        return null
      },
      headerTooltip: 'Next Outage End',
      initialHide: true,
      editable: false,
    },
    {
      field: 'outages',
      minWidth: 140,
      headerName: 'Next Schedule',
      valueGetter: ({ data }) => {
        const outageIdx = data?.outages?.[0]?.status === 'active' ? 1 : 0
        return startCase(data?.outages?.[outageIdx]?.schedule_local.schedule_type)
      },
      headerTooltip: 'Next Outage Schedule Type',
      initialHide: true,
      editable: false,
    },
    {
      field: 'net_or_gross_type',
      headerName: 'Net/Gross',
      minWidth: 100,
      initialHide: true,
    },
    {
      field: 'contract_lifting_valuation_method',
      headerName: 'Contract Lifting Valuation Method',
      minWidth: 100,
      initialHide: true,
    },
    {
      headerName: 'External Allocation',
      children: [
        {
          headerName: 'Allocation Remaining Amount Daily',
          valueGetter: ({ data }) =>
            data?.external_allocation?.allocation_remaining_amount_daily != null
              ? formatDecimal(data.external_allocation.allocation_remaining_amount_daily, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Allocation Remaining Amount Weekly',
          valueGetter: ({ data }) =>
            data?.external_allocation?.allocation_remaining_amount_weekly != null
              ? formatDecimal(data.external_allocation.allocation_remaining_amount_weekly, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Allocation Remaining Amount Monthly',
          valueGetter: ({ data }) =>
            data?.external_allocation?.allocation_remaining_amount_monthly != null
              ? formatDecimal(data.external_allocation.allocation_remaining_amount_monthly, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Allocation Remaining Amount Tri-Monthly',
          valueGetter: ({ data }) =>
            data?.external_allocation?.allocation_remaining_amount_tri_monthly != null
              ? formatDecimal(data.external_allocation.allocation_remaining_amount_tri_monthly, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Allocation Remaining Amount Custom',
          valueGetter: ({ data }) =>
            data?.external_allocation?.allocation_remaining_amount_custom != null
              ? formatDecimal(data.external_allocation.allocation_remaining_amount_custom, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Lifted Amount Daily',
          valueGetter: ({ data }) =>
            data?.external_allocation?.lifted_amount_daily != null
              ? formatDecimal(data.external_allocation.lifted_amount_daily, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Lifted Amount Weekly',
          valueGetter: ({ data }) =>
            data?.external_allocation?.lifted_amount_weekly != null
              ? formatDecimal(data.external_allocation.lifted_amount_weekly, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Lifted Amount Monthly',
          valueGetter: ({ data }) =>
            data?.external_allocation?.lifted_amount_monthly != null
              ? formatDecimal(data.external_allocation.lifted_amount_monthly, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Lifted Amount Tri-Monthly',
          valueGetter: ({ data }) =>
            data?.external_allocation?.lifted_amount_tri_monthly != null
              ? formatDecimal(data.external_allocation.lifted_amount_tri_monthly, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Lifted Amount Custom',
          valueGetter: ({ data }) =>
            data?.external_allocation?.lifted_amount_custom != null
              ? formatDecimal(data.external_allocation.lifted_amount_custom, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Refresh Amount Daily',
          valueGetter: ({ data }) =>
            data?.external_allocation?.refresh_amount_daily != null
              ? formatDecimal(data.external_allocation.refresh_amount_daily, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Refresh Amount Weekly',
          valueGetter: ({ data }) =>
            data?.external_allocation?.refresh_amount_weekly != null
              ? formatDecimal(data.external_allocation.refresh_amount_weekly, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Refresh Amount Monthly',
          valueGetter: ({ data }) =>
            data?.external_allocation?.refresh_amount_monthly != null
              ? formatDecimal(data.external_allocation.refresh_amount_monthly, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Refresh Amount Tri-Monthly',
          valueGetter: ({ data }) =>
            data?.external_allocation?.refresh_amount_tri_monthly != null
              ? formatDecimal(data.external_allocation.refresh_amount_tri_monthly, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Refresh Amount Custom',
          valueGetter: ({ data }) =>
            data?.external_allocation?.refresh_amount_custom != null
              ? formatDecimal(data.external_allocation.refresh_amount_custom, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Refresh Date Daily',
          ...defaultDateDef('external_allocation', formatters.DATE_TIME, false, 'refresh_date_daily'),
          minWidth: 150,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Refresh Date Weekly',
          ...defaultDateDef('external_allocation', formatters.DATE_TIME, false, 'refresh_date_weekly'),
          minWidth: 150,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Refresh Date Monthly',
          ...defaultDateDef('external_allocation', formatters.DATE_TIME, false, 'refresh_date_monthly'),
          minWidth: 150,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Refresh Date Tri-Monthly',
          ...defaultDateDef('external_allocation', formatters.DATE_TIME, false, 'refresh_date_tri_monthly'),
          minWidth: 150,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Refresh Date Custom',
          ...defaultDateDef('external_allocation', formatters.DATE_TIME, false, 'refresh_date_custom'),
          minWidth: 150,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Refresh Date Custom Days',
          valueGetter: ({ data }) =>
            data?.external_allocation?.refresh_date_custom_days != null
              ? formatDecimal(data.external_allocation.refresh_date_custom_days, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 150,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Scaled Start Amount Daily',
          valueGetter: ({ data }) =>
            data?.external_allocation?.scaled_start_amount_daily != null
              ? formatDecimal(data.external_allocation.scaled_start_amount_daily, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Scaled Start Amount Weekly',
          valueGetter: ({ data }) =>
            data?.external_allocation?.scaled_start_amount_weekly != null
              ? formatDecimal(data.external_allocation.scaled_start_amount_weekly, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Scaled Start Amount Monthly',
          valueGetter: ({ data }) =>
            data?.external_allocation?.scaled_start_amount_monthly != null
              ? formatDecimal(data.external_allocation.scaled_start_amount_monthly, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Scaled Start Amount Tri-Monthly',
          valueGetter: ({ data }) =>
            data?.external_allocation?.scaled_start_amount_tri_monthly != null
              ? formatDecimal(data.external_allocation.scaled_start_amount_tri_monthly, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
        {
          headerName: 'Scaled Start Amount Custom',
          valueGetter: ({ data }) =>
            data?.external_allocation?.scaled_start_amount_custom != null
              ? formatDecimal(data.external_allocation.scaled_start_amount_custom, 0, false)
              : null,
          cellStyle: { textAlign: 'right' },
          minWidth: 140,
          initialHide: true,
          editable: false,
        },
      ],
    },
  ]
}
